import { useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { useBranchStore } from '../store/branchStore';

/**
 * This hook pre-loads branches as soon as user is logged in
 * This ensures branches are available in the store before BranchSelector mounts
 */
export function useBranchesPreload(userId: string | undefined) {
  const hasPreloaded = useRef(false);

  useEffect(() => {
    if (!userId) {
      console.log('[useBranchesPreload] No userId, skipping preload');
      return;
    }

    // If we already have an active branch, no need to reload
    const currentBranch = useBranchStore.getState().activeBranch;
    if (currentBranch?.id) {
      console.log('[useBranchesPreload] Active branch already set:', currentBranch.name);
      hasPreloaded.current = true;
      return;
    }

    // Only fetch once per user
    if (hasPreloaded.current) {
      console.log('[useBranchesPreload] Already preloaded, skipping');
      return;
    }

    console.log('[useBranchesPreload] START - Preloading branches for user:', userId);
    const startTime = performance.now();

    const preloadBranches = async () => {
      try {
        console.log('[useBranchesPreload] Calling /branches API');
        const apiStart = performance.now();
        const res = await api.get<any>('/branches');
        const apiEnd = performance.now();
        console.log(`[useBranchesPreload] API call took ${(apiEnd - apiStart).toFixed(0)}ms, response:`, res);

        if (res.branches && Array.isArray(res.branches) && res.branches.length > 0) {
          const defaultBranch = res.branches.find((b: any) => b.isDefault);
          const branchToSet = defaultBranch || res.branches[0];

          console.log('[useBranchesPreload] Setting active branch:', branchToSet.name, branchToSet.id);
          useBranchStore.setState({ activeBranch: branchToSet });

          // Cache all branches in localStorage for fast access
          localStorage.setItem('cached_branches', JSON.stringify({
            branches: res.branches,
            defaultBranch: branchToSet,
            timestamp: Date.now(),
          }));
          console.log('[useBranchesPreload] Branches cached in localStorage');
          hasPreloaded.current = true;
          const endTime = performance.now();
          console.log(`[useBranchesPreload] COMPLETE - Total time: ${(endTime - startTime).toFixed(0)}ms`);
        } else {
          console.warn('[useBranchesPreload] No branches found in response or branches is not array');
        }
      } catch (error) {
        console.error('[useBranchesPreload] Failed to preload branches:', error);
        // Try to load from cache as fallback
        try {
          const cached = localStorage.getItem('cached_branches');
          if (cached) {
            const { defaultBranch } = JSON.parse(cached);
            if (defaultBranch) {
              console.log('[useBranchesPreload] Setting branch from cache fallback:', defaultBranch.name);
              useBranchStore.setState({ activeBranch: defaultBranch });
              hasPreloaded.current = true;
            }
          }
        } catch (cacheError) {
          console.error('[useBranchesPreload] Cache fallback also failed:', cacheError);
        }
      }
    };

    preloadBranches();
  }, [userId]);
}

